import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, ConfirmationModal, formatDate, useAlert, useModalControl } from '@frontend/design-system';
import { DATETIME_FORMATS } from '../../../../constants';
import { calendarEventsTrackingIds } from './calendar-events-tracking-ids';

interface DeleteAppointmentButtonProps {
  onDelete: () => void | Promise<void>;
}

export const DeleteAppointmentButton = ({ onDelete }: DeleteAppointmentButtonProps) => {
  const { t } = useTranslation('schedule');
  const modalControl = useModalControl();

  return (
    <>
      <Button
        iconName='trash'
        variant='tertiary'
        size='large'
        destructive
        css={deleteButtonStyles}
        {...modalControl.triggerProps}
        trackingId={calendarEventsTrackingIds.deleteAppointmentEventButton}
      >
        {t('Delete')}
      </Button>
      <ConfirmationModal
        destructive
        title={t('Delete Appointment')}
        message={t('This appointment will be removed from the schedule. This action cannot be undone.')}
        confirmLabel={t('Delete')}
        onConfirm={onDelete}
        {...modalControl.modalProps}
      />
    </>
  );
};

interface DeleteAppointmentButtonV3Props {
  calendarEventId: string;
  eventStartDate?: string;
  eventRefId?: string;
  onSuccess?: () => void;
}

export const DeleteAppointmentButtonV3 = ({
  calendarEventId,
  eventStartDate,
  eventRefId,
  onSuccess,
}: DeleteAppointmentButtonV3Props) => {
  const alerts = useAlert();
  const { t } = useTranslation('schedule');

  const deleteCalendarsEventMutation = SchedulerV3.Mutations.useDeleteCalendarsEventMutation();

  const deleteAppointment = async () => {
    try {
      await deleteCalendarsEventMutation.mutateAsync({
        eventId: calendarEventId,
        existingStartDate: formatDate(eventStartDate, DATETIME_FORMATS.ISO_DATE),
        referenceId: eventRefId,
      });
      alerts.success(t('Successfully deleted appointment'));
      onSuccess?.();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alerts.error(t('Failed to delete appointment. Please try again.'));
    }
  };

  return <DeleteAppointmentButton onDelete={deleteAppointment} />;
};

const deleteButtonStyles = css({
  position: 'absolute',
  bottom: theme.spacing(4),
});
